
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import LogoUpload from "./company/LogoUpload";
import { 
  CompanyNameField, 
  LocationField, 
  BioField, 
  StaffSizeField,
  WebsiteField,
  TwitterField,
  InstagramField,
  LinkedinField
} from "./company/CompanyFormFields";
import FormActions from "./company/FormActions";
import { formSchema, CompanyFormValues, australianLocations } from "./company/CompanyFormSchema";
import { getStandardizedLocation } from "@/utils/locationHelpers";

export type { CompanyFormValues };

interface CompanyFormProps {
  onSubmit: (data: CompanyFormValues) => void;
  onCancel: () => void;
  submitButtonText?: string;
  hideCancel?: boolean;
  initialValues?: {
    name?: string;
    location?: string;
    bio?: string;
    staffSize?: string;
    logo?: string;
    website?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    linkedinUrl?: string;
  };
}

const CompanyForm = ({ 
  onSubmit, 
  onCancel, 
  submitButtonText = "Save Company", 
  hideCancel = false,
  initialValues 
}: CompanyFormProps) => {
  const [logoPreview, setLogoPreview] = React.useState<string | null>(initialValues?.logo || null);
  
  // Ensure location is standardized
  const standardizedLocation = initialValues?.location 
    ? getStandardizedLocation(initialValues.location)
    : australianLocations[0];
  
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialValues?.name || "",
      location: standardizedLocation,
      bio: initialValues?.bio || "",
      staffSize: initialValues?.staffSize || "",
      website: initialValues?.website || "",
      twitterUrl: initialValues?.twitterUrl || "",
      instagramUrl: initialValues?.instagramUrl || "",
      linkedinUrl: initialValues?.linkedinUrl || "",
    },
  });

  const handleLogoChange = (file: File | undefined) => {
    form.setValue("logo", file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const handleFormSubmit = (data: CompanyFormValues) => {
    onSubmit({
      ...data,
      // Pass along the logo preview so the parent component can use it
      logo: data.logo,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        {!initialValues?.name && (
          <CompanyNameField control={form.control} />
        )}
        <LocationField control={form.control} />
        <BioField control={form.control} />
        <StaffSizeField control={form.control} />
        
        <div className="border-t pt-6 mt-8">
          <h3 className="text-lg font-medium mb-4">Online Presence</h3>
          <div className="space-y-6">
            <WebsiteField control={form.control} />
            <TwitterField control={form.control} />
            <InstagramField control={form.control} />
            <LinkedinField control={form.control} />
          </div>
        </div>
        
        {!hideCancel && initialValues?.logo && (
          <div className="border-t pt-6 mt-8">
            <h3 className="text-lg font-medium mb-4">Company Logo</h3>
            <LogoUpload 
              logoPreview={logoPreview} 
              onLogoChange={handleLogoChange} 
            />
          </div>
        )}
        
        <FormActions 
          onCancel={onCancel} 
          submitButtonText={submitButtonText}
          hideCancel={hideCancel}
        />
      </form>
    </Form>
  );
};

export default CompanyForm;
